import React, { createContext, useCallback, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';

import api from '../services/api';

type AuthContextProps ={
	children: React.ReactNode;
}
interface UserData {
	id: string;
	nome: string;
	email: string;
	doc: string;
	phone: string;
	role: string;
	url_avatar: string;
	comissao: number;
}
interface EnderecoData {
	id: string;
	cep: string;
	uf: string;
	cid: string;
	cidcod: string;
	bai: string;
	endereco: string;
	num: string;
	comp: string;
	ponto_ref: string;
	formatted_address: string;
	location: {
		lat: number;
		lng: number;
	};
}
interface LojaData {
	id: string;
	nome: string;
	url_avatar: string;
	url_page: string;
	fantazia: string;
	fone: string;
	endereco: EnderecoData;
}
interface AuthState {
	token: string;
	user: UserData;
	lojas: LojaData[];
	company: LojaData;
}
interface SignInCredentials {
	email: string;
	password: string;
}
interface AuthContexData {
	user: UserData;
	lojas: LojaData[];
	company: LojaData;
	token: string;
	signIn(credentials: SignInCredentials): Promise<UserData>;
	signOut(): void; // (next: string): void;
	updateUser(user: UserData): void;
	setCompany(company: LojaData): void;
}

const AuthContext = createContext<AuthContexData>({} as AuthContexData);

const AuthProvider: React.FC<AuthContextProps> = ({ children }) => {
	const history = useHistory();

	const [data, setData] = useState<AuthState>(() => {
		const token = localStorage.getItem('@ControleRazio:token');
		const user = localStorage.getItem('@ControleRazio:user');
		const lojas = localStorage.getItem('@ControleRazio:lojas');
		const company = localStorage.getItem('@ControleRazio:company');

    const auxData = {} as AuthState;

    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      auxData.token = token;
    }

    if (user)
      auxData.user = JSON.parse(user);

    if (lojas)
      auxData.lojas = JSON.parse(lojas)

    if (company) {
      api.defaults.headers.common['company'] = JSON.parse(company).id; // Number(JSON.parse(company).id);
      auxData.company = JSON.parse(company);
    }

    return auxData;
	});

	const signIn = useCallback(async ({ email, password }: SignInCredentials) => {
			const response = await api.post('controle/pedidos/sessions', {
				email,
				password,
			});

			const { user, token, lojas } = response.data;
			const company = lojas[0];

			localStorage.setItem('@ControleRazio:token', token);
			localStorage.setItem('@ControleRazio:user', JSON.stringify(user));
			localStorage.setItem('@ControleRazio:lojas', JSON.stringify(lojas));
			localStorage.setItem('@ControleRazio:company', JSON.stringify(company));

			api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
			api.defaults.headers.common['company'] = company.id

			setData({ token, user, lojas, company });

			history.push('/client');

			return user;
	}, [history]);

	const signOut = useCallback(() => {
		setData({} as AuthState);
		localStorage.removeItem('@ControleRazio:token');
		localStorage.removeItem('@ControleRazio:user');
		localStorage.removeItem('@ControleRazio:lojas');
		localStorage.removeItem('@ControleRazio:company');
	}, []);

	const updateUser = useCallback(async (user: UserData) => {
		setData(item => ({ ...item, user }));
		localStorage.setItem('@CardapioRazio:user', JSON.stringify(user));
	}, []);

	const setCompany = useCallback((loja: LojaData) => {
		api.defaults.headers.common['company'] = loja.id;

		setData(data => {
			return { ...data, company: loja }
		});

		localStorage.setItem('@ControleRazio:company', JSON.stringify(loja));
	}, []);

	return (
		<AuthContext.Provider
			value={{
				user: data.user,
				lojas: data.lojas,
				company: data.company,
				token: data.token,
				signIn,
				signOut,
				updateUser,
				setCompany,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

function useAuth(): AuthContexData {
	const context = useContext(AuthContext);

	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider');
	}

	return context;
}

export { AuthProvider, useAuth };
